import React, { useEffect, useMemo, useState } from "react";
import {
  createBrowserRouter,
  Navigate,
  Route,
  RouterProvider,
  Routes,
  useParams,
} from "react-router-dom";
import { useSelector } from "react-redux";
import Login from "../pages/auth/Login";
import MainLayout from "../components/layout/MainLayout";
import POSLayout from "../components/layout/POSLayout";
import ProtectedRoute from "../components/auth/ProtectedRoute";
import SectionAccessGate from "../components/auth/SectionAccessGate";
import SetupGate from "../components/auth/SetupGate";
import Card from "../components/common/Card";
import Alert from "../components/common/Alert";
import Button from "../components/common/Button";
import { UiSecurityProvider } from "../context/UiSecurityContext";
import Categories from "../pages/menu/Categories";
import Stations from "../pages/menu/Stations";
import Items from "../pages/menu/Items";
import ItemDetail from "../pages/menu/ItemDetail";
import StationAssignment from "../pages/menu/StationAssignment";
import Reservations from "../pages/menu/Reservations";
import Floors from "../pages/floorplan/Floors";
import Sections from "../pages/floorplan/Sections";
import Tables from "../pages/floorplan/Tables";
import UserList from "../pages/users/UserList";
import UserForm from "../pages/users/UserForm";
import AccessLevels from "../pages/users/AccessLevels";
import LocalDevices from "../pages/devices/LocalDevices";
import Kiosks from "../pages/devices/Kiosks";
import Terminals from "../pages/devices/Terminals";
import DeviceHealth from "../pages/devices/DeviceHealth";
import TaxRates from "../pages/settings/TaxRates";
import FulfillmentSettings from "../pages/settings/FulfillmentSettings";
import PrintersSettings from "../pages/settings/PrintersSettings";
import PrinterForm from "../pages/settings/PrinterForm";
import ConfigureStore from "../pages/settings/ConfigureStore";
import Tills from "../pages/settings/Tills";
import TillDetail from "../pages/settings/TillDetail";
import Faq from "../pages/settings/Faq";
import DiningRoomControl from "../pages/pos/DiningRoomControl";
// POS Dine-In Pages (Real API)
import OrderList from "../pages/pos/OrderList";
import OrderEditor from "../pages/pos/OrderEditor";
import SplitInvoice from "../pages/pos/SplitInvoice";
import Payment from "../pages/pos/Payment";
import DineInEntry from "../pages/pos/DineInEntry";
import TakeawayOrderEditor from "../pages/pos/TakeawayOrderEditor";
import DineInOrderEditor from "../pages/pos/DineInOrderEditor";
import HeldOrders from "../pages/pos/HeldOrders";
import CashUp from "../pages/pos/CashUp";
import CashMovements from "../pages/pos/CashMovements";
import ShiftHistory from "../pages/pos/ShiftHistory";
import ShiftHistoryDetail from "../pages/pos/ShiftHistoryDetail";
import ChangePaymentMethod from "../pages/pos/ChangePaymentMethod";
import CashingUpSessions from "../pages/pos/CashingUpSessions";
import CashingUpSessionDetail from "../pages/pos/CashingUpSessionDetail";
import CashReconciliation from "../pages/pos/CashReconciliation";
import CashRefund from "../pages/pos/CashRefund";
import OrderLifecycleDetail from "../pages/reporting/OrderLifecycleDetail";
import FulfillmentOrdersReport from "../pages/reporting/FulfillmentOrdersReport";
import FulfillmentItemsReport from "../pages/reporting/FulfillmentItemsReport";
import FulfillmentOrderDetail from "../pages/fulfillment/FulfillmentOrderDetail";
import PendingPayments from "../pages/fulfillment/PendingPayments";
import LicenseExpired from "../pages/licenses/LicenseExpired";
import { useStoreFeatures } from "../hooks/useStoreFeatures";
import { PERMISSIONS, hasPermission } from "../utils/permissions";
import { isChannelOrderingBlocked } from "../utils/orderBlocking";
import {
  isLicenseExpiredFlagSet,
  subscribeToLicenseExpiredChanges,
} from "../utils/licenseExpired";
import { getErrorMessage } from "../utils/errorMessage";
// import CampaignList from "../pages/upselling/CampaignList";
// import CampaignForm from "../pages/upselling/CampaignForm";
// import CampaignDetail from "../pages/upselling/CampaignDetail";

const FeatureGate = ({
  enabled,
  allowed,
  children,
  fallbackPath = "/login",
  storeFeaturesState,
}) => {
  if (!enabled) {
    return children;
  }

  const { isLoading, isFetching, isError, error, hasFeatures, refetch } =
    storeFeaturesState || {};

  if (isLoading || isFetching) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <Card className="w-full max-w-md text-center">
          <div className="text-sm text-slate-500">Loading store features...</div>
        </Card>
      </div>
    );
  }

  if (isError || !hasFeatures) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <Card className="w-full max-w-xl space-y-4">
          <h1 className="text-lg font-semibold text-slate-900">
            Unable to load store features
          </h1>
          <Alert tone="error">
            {getErrorMessage(error, "Failed to load store features.")}
          </Alert>
          <Button size="sm" onClick={() => refetch && refetch()}>
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to={fallbackPath} replace />;
  }

  return children;
};

const CashSessionRedirect = ({ basePath }) => {
  const { cashSessionId } = useParams();
  return <Navigate to={`${basePath}/${cashSessionId}`} replace />;
};

const AppRouteContent = () => {
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = Boolean(token && user);

  const [licenseExpired, setLicenseExpired] = useState(() =>
    isLicenseExpiredFlagSet(),
  );

  const storeFeaturesState = useStoreFeatures(isAuthenticated);
  const features = storeFeaturesState.features;
  const currentUser = user;
  const posDineInMode = features?.posDineInMode
    ? String(features.posDineInMode).toUpperCase()
    : null;
  const posEnableTakeaway = features?.posEnableTakeaway !== false;
  const isOrderBlocked = isChannelOrderingBlocked(features, "POS");
  const canTableDineIn = posDineInMode === "TABLE_DINE_IN";
  const canNoTableDineIn = posDineInMode === "DINE_IN";
  const canManageFloorplan =
    canTableDineIn ||
    (Boolean(features?.enableQr) &&
      hasPermission(currentUser, PERMISSIONS.QR_VIEW) &&
      (posDineInMode === "TABLE_DINE_IN" || posDineInMode === "DINE_IN"));
  const featureAllowed = useMemo(
    () => ({
      pos:
        Boolean(features?.enablePos) &&
        hasPermission(currentUser, PERMISSIONS.POS_VIEW),
      kiosk:
        Boolean(features?.enableKiosk) &&
        hasPermission(currentUser, PERMISSIONS.KIOSK_VIEW),
    }),
    [features, currentUser],
  );

  useEffect(() => {
    const updateFlag = () => setLicenseExpired(isLicenseExpiredFlagSet());
    const unsubscribe = subscribeToLicenseExpiredChanges(updateFlag);
    return () => {
      unsubscribe();
    };
  }, []);

  if (licenseExpired) {
    return (
      <Routes>
        <Route path="/license-expired" element={<LicenseExpired />} />
        <Route path="*" element={<Navigate to="/license-expired" replace />} />
      </Routes>
    );
  }

  return (
    <UiSecurityProvider>
      <SetupGate>
        <Routes>
          <Route path="/license-expired" element={<LicenseExpired />} />
          <Route path="/setup/store" element={<ConfigureStore />} />
          <Route path="/login" element={<Login />} />

        {/* POS Routes - Updated with Real API */}
        <Route
          path="/pos"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <POSLayout cashSessionAutoPrompt={false}>
                  {!posDineInMode ? (
                    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
                      <Card className="w-full max-w-md text-center">
                        <div className="text-sm text-slate-500">
                          Loading POS...
                        </div>
                      </Card>
                    </div>
                  ) : canTableDineIn ? (
                    <Navigate to="/pos/table" replace />
                  ) : canNoTableDineIn ? (
                    <Navigate to="/pos/dinein" replace />
                  ) : posEnableTakeaway ? (
                    <Navigate to="/pos/takeaway" replace />
                  ) : (
                    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
                      <Card className="w-full max-w-md space-y-2 text-center">
                        <h1 className="text-lg font-semibold text-slate-900">
                          POS is disabled
                        </h1>
                        <div className="text-sm text-slate-500">
                          This store has dine-in and takeaway disabled.
                        </div>
                      </Card>
                    </div>
                  )}
                </POSLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/table"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <FeatureGate
                  enabled={isAuthenticated}
                  allowed={canTableDineIn}
                  fallbackPath="/pos"
                  storeFeaturesState={storeFeaturesState}
                >
                  <POSLayout cashSessionAutoPrompt={false}>
                    <DiningRoomControl />
                  </POSLayout>
                </FeatureGate>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/held-orders"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <div className="min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800 transition-colors duration-200">
                  <div className="flex-1">
                    <HeldOrders />
                  </div>
                </div>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/cashing-up/sessions"
          element={
            <ProtectedRoute>
              <Navigate to="/cash-management/till-sessions" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/cash-up"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <POSLayout allowPageScroll>
                  <CashUp />
                </POSLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/change-payment-method"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <POSLayout>
                  <ChangePaymentMethod />
                </POSLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-management/till-sessions"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <MainLayout>
                  <CashingUpSessions />
                </MainLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-management/refunds"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <MainLayout>
                  <CashRefund />
                </MainLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-management/refunds/:orderId"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <MainLayout>
                  <CashRefund />
                </MainLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/cash-movements"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <POSLayout allowPageScroll>
                  <CashMovements />
                </POSLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/cash-movements/history"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <POSLayout allowPageScroll>
                  <ShiftHistory />
                </POSLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/cash-movements/history/:cashSessionId"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <POSLayout allowPageScroll>
                  <ShiftHistoryDetail />
                </POSLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-management/cash-movements"
          element={
            <ProtectedRoute>
              <Navigate to="/pos/cash-movements" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/reconciliation"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <POSLayout>
                  <CashReconciliation />
                </POSLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-management/reconciliation"
          element={
            <ProtectedRoute>
              <Navigate to="/pos/reconciliation" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-management/till-sessions/:cashSessionId"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <MainLayout>
                  <CashingUpSessionDetail />
                </MainLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/cashing-up/sessions/:cashSessionId"
          element={
            <ProtectedRoute>
              <CashSessionRedirect basePath="/cash-management/till-sessions" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/cashing-up/day-close"
          element={
            <ProtectedRoute>
              <Navigate to="/pos/cash-up" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/day-close"
          element={
            <ProtectedRoute>
              <Navigate to="/pos/cash-up" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-management/day-close"
          element={
            <ProtectedRoute>
              <Navigate to="/cash-management/till-sessions" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/dinein"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <FeatureGate
                  enabled={isAuthenticated}
                  allowed={canNoTableDineIn}
                  fallbackPath="/pos"
                  storeFeaturesState={storeFeaturesState}
                >
                  <POSLayout cashSessionAutoPrompt={false}>
                    {isOrderBlocked ? (
                      <DineInOrderEditor />
                    ) : (
                      <DineInEntry />
                    )}
                  </POSLayout>
                </FeatureGate>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/orders/:orderId"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <OrderEditor />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/takeaway"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <FeatureGate
                  enabled={isAuthenticated}
                  allowed={posEnableTakeaway}
                  fallbackPath="/pos"
                  storeFeaturesState={storeFeaturesState}
                >
                  <TakeawayOrderEditor entryVariant="TAKEAWAY" />
                </FeatureGate>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/takeaway/:orderId"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <FeatureGate
                  enabled={isAuthenticated}
                  allowed={posEnableTakeaway}
                  fallbackPath="/pos"
                  storeFeaturesState={storeFeaturesState}
                >
                  <TakeawayOrderEditor />
                </FeatureGate>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/dinein/:orderId"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <FeatureGate
                  enabled={isAuthenticated}
                  allowed={canNoTableDineIn}
                  fallbackPath="/pos"
                  storeFeaturesState={storeFeaturesState}
                >
                  <POSLayout cashSessionAutoPrompt={false}>
                    <DineInOrderEditor />
                  </POSLayout>
                </FeatureGate>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/orders/:orderId/split"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <SplitInvoice />
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/orders/:orderId/payment"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <POSLayout>
                  <Payment />
                </POSLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/invoices/:invoiceId/payment"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <POSLayout>
                  <Payment />
                </POSLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />

        {/* Floorplan Routes */}
        <Route
          path="/floorplan/floors"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={canManageFloorplan}
                fallbackPath="/pos"
                storeFeaturesState={storeFeaturesState}
              >
                <MainLayout>
                  <Floors />
                </MainLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/floorplan/sections"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={canManageFloorplan}
                fallbackPath="/pos"
                storeFeaturesState={storeFeaturesState}
              >
                <MainLayout>
                  <Sections />
                </MainLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/floorplan/tables"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={canManageFloorplan}
                fallbackPath="/pos"
                storeFeaturesState={storeFeaturesState}
              >
                <MainLayout>
                  <Tables />
                </MainLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />

        {/* Menu Routes */}
        <Route
          path="/menu/categories"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Categories />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/items"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Items />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/stations"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Stations />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/station-assignment"
          element={
            <ProtectedRoute>
              <MainLayout>
                <StationAssignment />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pos/reservations"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.pos}
                fallbackPath="/floorplan/floors"
                storeFeaturesState={storeFeaturesState}
              >
                <FeatureGate
                  enabled={isAuthenticated}
                  allowed={canTableDineIn}
                  fallbackPath="/pos"
                  storeFeaturesState={storeFeaturesState}
                >
                  <POSLayout>
                    <Reservations />
                  </POSLayout>
                </FeatureGate>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/menu/items/:itemId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ItemDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* User Management Routes */}
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SectionAccessGate sectionKey="users">
                  <UserList />
                </SectionAccessGate>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/new"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SectionAccessGate sectionKey="users">
                  <UserForm />
                </SectionAccessGate>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/:userId/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SectionAccessGate sectionKey="users">
                  <UserForm />
                </SectionAccessGate>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users/access-levels"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SectionAccessGate sectionKey="users">
                  <AccessLevels />
                </SectionAccessGate>
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Device Management Routes */}
        <Route
          path="/devices/health"
          element={
            <ProtectedRoute>
              <MainLayout>
                <DeviceHealth />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/local-devices"
          element={
            <ProtectedRoute>
              <MainLayout>
                <LocalDevices />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/kiosks"
          element={
            <ProtectedRoute>
              <FeatureGate
                enabled={isAuthenticated}
                allowed={featureAllowed.kiosk}
                fallbackPath="/devices/local-devices"
                storeFeaturesState={storeFeaturesState}
              >
                <MainLayout>
                  <Kiosks />
                </MainLayout>
              </FeatureGate>
            </ProtectedRoute>
          }
        />
        <Route
          path="/devices/terminals"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Terminals />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Reporting Routes */}
        <Route
          path="/reporting"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Navigate to="/reporting/orders" replace />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/orders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <FulfillmentOrdersReport />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/items"
          element={
            <ProtectedRoute>
              <MainLayout>
                <FulfillmentItemsReport />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reporting/order-lifecycle/:orderId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <OrderLifecycleDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fulfillment/orders"
          element={
            <ProtectedRoute>
              <Navigate to="/reporting/orders" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fulfillment/items"
          element={
            <ProtectedRoute>
              <Navigate to="/reporting/items" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/fulfillment/pending-payments"
          element={
            <ProtectedRoute>
              <MainLayout>
                <PendingPayments />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fulfillment/orders/:orderId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <FulfillmentOrderDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/fulfillment/pos-orders"
          element={
            <ProtectedRoute>
              <MainLayout>
                <OrderList />
              </MainLayout>
            </ProtectedRoute>
          }
        />

        {/* Upselling Routes - Deprecated in Edge; kept commented for reference */}
        {/*
        <Route
          path="/upselling/campaigns"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CampaignList />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/upselling/campaigns/new"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CampaignForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/upselling/campaigns/:campaignId/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CampaignForm />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/upselling/campaigns/:campaignId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CampaignDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        */}

        {/* Deprecated Edge catalog-management routes */}
        <Route path="/upselling/*" element={<Navigate to="/menu/items" replace />} />

        {/* Settings Routes */}
        <Route
          path="/settings/configure-store"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ConfigureStore />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/configure-store/reconfigure"
          element={
            <ProtectedRoute>
              <MainLayout>
                <ConfigureStore />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/tax-rates"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TaxRates />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/fulfillment"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SectionAccessGate sectionKey="generalSettings">
                  <FulfillmentSettings />
                </SectionAccessGate>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/printers"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SectionAccessGate sectionKey="printers">
                  <PrintersSettings />
                </SectionAccessGate>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/printers/new"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SectionAccessGate sectionKey="printers">
                  <PrinterForm />
                </SectionAccessGate>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/printers/:printerId/edit"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SectionAccessGate sectionKey="printers">
                  <PrinterForm />
                </SectionAccessGate>
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/tills"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Tills />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/tills/:tillId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TillDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-management/tills"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Tills />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-management/tills/:tillId"
          element={
            <ProtectedRoute>
              <MainLayout>
                <TillDetail />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/faq"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Faq />
              </MainLayout>
            </ProtectedRoute>
          }
        />

            <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </SetupGate>
    </UiSecurityProvider>
  );
};

const router = createBrowserRouter([
  {
    path: "*",
    element: <AppRouteContent />,
  },
]);

const AppRoutes = () => <RouterProvider router={router} />;

export default AppRoutes;
